import { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import { toast } from "sonner";
import { Broadcast, Plant, SquaresFour, Info, Play, Pause } from "@phosphor-icons/react";
import { LineChart, Line, Area, ComposedChart, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { getEeLandcover, getMunicipio, getAgriLstSeries } from "@/lib/api";
import { Sheet, SheetToggles } from "@/components/rs/mobileSheet";
import { CHART } from "@/lib/chartTheme";

const DATASETS = {
  esa: { label: "ESA WorldCover", years: [2020, 2021], note: "Validado, 11 clases (2020 y 2021)." },
  esri: { label: "Esri Land Cover", years: [2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024], note: "Serie anual 2017–2024 (animable)." },
};

export default function LandCoverView() {
  const [dataset, setDataset] = useState("esa");
  const [year, setYear] = useState(2021);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [muni, setMuni] = useState(null);
  const [cropOnly, setCropOnly] = useState(false);
  const [agriLst, setAgriLst] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [playing, setPlaying] = useState(false);
  const cacheRef = useRef({});
  const years = DATASETS[dataset].years;

  useEffect(() => { getMunicipio().then(setMuni).catch(() => {}); }, []);
  useEffect(() => { getAgriLstSeries("landsat").then(setAgriLst).catch(() => setAgriLst([])); }, []);

  useEffect(() => {
    const k = `${dataset}:${year}`;
    if (cacheRef.current[k]) { setData(cacheRef.current[k]); setLoading(false); return; }
    setLoading(true); setData(null);
    getEeLandcover(year, dataset)
      .then((d) => { cacheRef.current[k] = d; setData(d); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Error de Earth Engine"))
      .finally(() => setLoading(false));
  }, [year, dataset]);

  // Animación: avanza de año en año (Esri Land Cover Explorer style)
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => {
      setYear((y) => years[(years.indexOf(y) + 1) % years.length]);
    }, 1600);
    return () => clearInterval(id);
  }, [playing, years]);

  // Precarga todos los años del conjunto para que la animación sea fluida
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const y of DATASETS[dataset].years) {
        const k = `${dataset}:${y}`;
        if (cacheRef.current[k]) continue;
        try { const d = await getEeLandcover(y, dataset); if (!cancelled) cacheRef.current[k] = d; }
        catch { /* silencioso */ }
        if (cancelled) return;
      }
    })();
    return () => { cancelled = true; };
  }, [dataset]);

  const changeDataset = (ds) => {
    setPlaying(false);
    setDataset(ds);
    const ys = DATASETS[ds].years;
    setYear((y) => (ys.includes(y) ? y : ys[ys.length - 1]));
  };

  const tileUrl = data ? (cropOnly ? data.crop_tile_url : data.tile_url) : null;

  return (
    <div className="flex-1 flex min-h-0">
      <Sheet open={sheet === "controls"} side="left" testid="landcover-sidebar" className="lg:w-[300px] space-y-5">
        <div>
          <div className="plex text-sm font-medium mb-1 flex items-center gap-1.5"><SquaresFour size={15} /> Uso y cobertura del suelo</div>
          <p className="text-[11px] text-[#475569] leading-relaxed">
            Cobertura del suelo a 10 m (<span className="text-[#1f2937]">{DATASETS[dataset].label}</span>)
            para identificar las <span className="text-amber-500">zonas agrícolas</span> de Constanza.
            Cambia de conjunto de datos y anima el avance año por año.
          </p>
        </div>

        <section>
          <div className="label-caps mb-2">Conjunto de datos</div>
          <div className="flex gap-2">
            <button onClick={() => changeDataset("esa")} data-testid="landcover-dataset-esa"
              className={`flex-1 text-xs py-2 rounded-md border transition-colors ${dataset === "esa" ? "border-[#4a9b3c] bg-[#eef7ed] text-[#2d6624] font-medium" : "border-[#e5e7eb] text-[#334155] hover:bg-[#eef7ed]"}`}>
              ESA WorldCover
            </button>
            <button onClick={() => changeDataset("esri")} data-testid="landcover-dataset-esri"
              className={`flex-1 text-xs py-2 rounded-md border transition-colors ${dataset === "esri" ? "border-[#4a9b3c] bg-[#eef7ed] text-[#2d6624] font-medium" : "border-[#e5e7eb] text-[#334155] hover:bg-[#eef7ed]"}`}>
              Esri Land Cover
            </button>
          </div>
          <p className="text-[10px] text-[#475569] mt-1.5">{DATASETS[dataset].note}</p>
        </section>

        <section>
          <div className="label-caps mb-2 flex items-center justify-between">
            <span>Año</span><span className="mono text-[#3f8e33] text-lg" data-testid="landcover-year-label">{data?.year ?? year}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPlaying((p) => !p)} data-testid="landcover-play"
              title={playing ? "Pausar animación" : "Animar avance por año"}
              className="shrink-0 w-9 h-9 flex items-center justify-center rounded-md bg-[#4a9b3c] hover:bg-[#3f8e33] text-white transition-colors">
              {playing ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
            </button>
            <input type="range" data-testid="landcover-year-slider"
              min={0} max={years.length - 1} step={1} value={years.indexOf(year)}
              onChange={(e) => { setPlaying(false); setYear(years[Number(e.target.value)]); }}
              className="flex-1 accent-[#4a9b3c] cursor-pointer" />
          </div>
          <div className="flex justify-between mono text-[10px] text-[#475569] mt-1">
            <span>{years[0]}</span><span>{years[years.length - 1]}</span>
          </div>
          {dataset === "esri" && <p className="text-[10px] text-[#475569] mt-1.5">Pulsa ▶ para animar el avance de la cobertura (2017–2024).</p>}
        </section>

        <section>
          <div className="label-caps mb-2">Vista</div>
          <div className="flex gap-2">
            <button onClick={() => setCropOnly(false)} data-testid="landcover-all"
              className={`flex-1 flex items-center justify-center gap-1.5 text-xs py-2 rounded-md border transition-colors ${!cropOnly ? "border-[#4a9b3c] bg-[#eef7ed] text-[#2d6624]" : "border-[#e5e7eb] text-[#334155] hover:bg-[#eef7ed]"}`}>
              <SquaresFour size={13} /> Todas las clases
            </button>
            <button onClick={() => setCropOnly(true)} data-testid="landcover-crops"
              className={`flex-1 flex items-center justify-center gap-1.5 text-xs py-2 rounded-md border transition-colors ${cropOnly ? "border-amber-500 bg-amber-50 text-amber-700" : "border-[#e5e7eb] text-[#334155] hover:bg-[#eef7ed]"}`}>
              <Plant size={13} /> Solo agrícola
            </button>
          </div>
        </section>

        {data?.meta && (
          <section className="pt-2 border-t border-[#e5e7eb]" data-testid="landcover-meta">
            <div className="label-caps mb-2 flex items-center gap-1.5"><Info size={13} /> Información de la capa</div>
            <dl className="text-[11px] text-[#1f2937] space-y-1">
              {[["Fuente", data.meta.fuente], ["Colección", data.meta.coleccion], ["Sensor", data.meta.sensor],
                ["Período", data.meta.periodo], ["Resolución", data.meta.resolucion], ["Procesamiento", data.meta.procesamiento]].map(([k, v]) => (
                <div className="flex gap-2" key={k}><dt className="text-[#475569] shrink-0" style={{ minWidth: 74 }}>{k}</dt><dd className="text-[#1f2937]">{v}</dd></div>
              ))}
            </dl>
          </section>
        )}
      </Sheet>

      <main className="flex-1 relative min-w-0" data-testid="landcover-map-area">
        {loading && (
          <div className="absolute inset-0 z-[600] flex items-center justify-center bg-[#f8faf9]/75 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3">
              <Broadcast size={26} className="text-[#3f8e33] animate-pulse" />
              <span className="text-xs mono text-[#334155]">Cargando cobertura del suelo…</span>
            </div>
          </div>
        )}
        {muni && (
          <div data-testid="landcover-map" className="lc-map" style={{ height: "100%", width: "100%" }}>
          <MapContainer center={[muni.center.lat, muni.center.lon]} zoom={11} scrollWheelZoom={true} maxZoom={19}
            bounds={[[muni.bbox.lat_min, muni.bbox.lon_min], [muni.bbox.lat_max, muni.bbox.lon_max]]}
            style={{ height: "100%", width: "100%" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" subdomains="abc" className="lc-basemap"
              attribution="&copy; <a href='https://www.openstreetmap.org/copyright' target='_blank'>OpenStreetMap</a> contributors" maxZoom={19} />
            {tileUrl && <TileLayer key={tileUrl} url={tileUrl} opacity={0.92} maxNativeZoom={18} maxZoom={19}
              attribution={dataset === "esri"
                ? "Cobertura: <a href='https://livingatlas.arcgis.com/landcoverexplorer' target='_blank'>Esri Land Cover</a> 10 m (Impact Observatory) vía Google Earth Engine"
                : "Cobertura: <a href='https://esa-worldcover.org' target='_blank'>ESA WorldCover</a> 10 m (CC BY 4.0) vía Google Earth Engine"} />}
            <GeoJSON data={muni.geojson} style={{ color: "#22d3ee", weight: 2, fill: false, dashArray: "6 4" }} />
          </MapContainer>
          </div>
        )}
        {data?.classes && (
          <div className="absolute bottom-5 left-5 z-[500] glass rounded-md px-4 py-3 w-[230px]" data-testid="landcover-legend">
            <div className="label-caps mb-2">Clases de cobertura ({data.year})</div>
            <div className="space-y-1">
              {data.classes.map((c) => (
                <div key={c.id} className="flex items-center gap-2 text-[11px]">
                  <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: c.color }} />
                  <span className={`flex-1 ${c.is_crop ? "text-amber-600 font-medium" : "text-[#1f2937]"}`}>{c.name}</span>
                  <span className="mono text-[#334155]">{c.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <Sheet open={sheet === "data"} side="right" testid="landcover-stats" className="lg:w-[320px] space-y-4">
        <div className="plex text-sm font-medium">Superficie por clase (municipio)</div>
        {data?.classes ? (
          <>
            <div className="border border-amber-300 bg-amber-50 p-4 rounded-md" data-testid="landcover-crop-area">
              <div className="label-caps text-amber-600 flex items-center gap-1.5"><Plant size={13} /> Zonas agrícolas (cultivos)</div>
              <div className="mono text-3xl font-semibold mt-1 text-amber-700">{data.crop_area_ha} <span className="text-sm text-[#475569]">ha</span></div>
              <div className="text-[11px] text-[#475569] mt-1">
                {(data.classes.find((c) => c.is_crop)?.pct ?? 0)}% del municipio clasificado como cultivos.
              </div>
            </div>
            <div className="space-y-2">
              {data.classes.map((c) => (
                <div key={c.id} className="flex items-center gap-2.5" data-testid={`landcover-class-${c.id}`}>
                  <span className="w-3.5 h-3.5 rounded-sm shrink-0" style={{ background: c.color }} />
                  <span className={`flex-1 text-[13px] ${c.is_crop ? "text-amber-600 font-medium" : "text-[#1f2937]"}`}>{c.name}</span>
                  <span className="mono text-[13px] text-[#1f2937]">{c.area_ha.toLocaleString()} ha</span>
                  <span className="mono text-[11px] text-[#475569] w-10 text-right">{c.pct}%</span>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-[#475569] leading-relaxed pt-2 border-t border-[#e5e7eb]">
              {DATASETS[dataset].label} 10 m (Sentinel-2). La máscara agrícola de la pestaña
              <span className="text-[#3f8e33]"> Cambios</span> usa ESA WorldCover (Cropland).
            </div>

            <div className="pt-3 border-t border-[#e5e7eb]" data-testid="agri-lst-chart">
              <div className="label-caps mb-1 flex items-center gap-1.5"><Plant size={13} style={{ color: CHART.lst }} /> LST y NDVI medios anuales en zonas agrícolas</div>
              <p className="text-[10px] text-[#475569] mb-2">Temperatura de superficie (naranja) y NDVI (verde) promediados solo sobre los cultivos, Landsat 30 m, 2000–2024.</p>
              {!agriLst && <div className="text-[11px] text-[#334155] animate-pulse">Cargando serie…</div>}
              {agriLst && agriLst.length > 0 && (
                <div style={{ height: 165 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={agriLst} margin={{ top: 5, right: 4, left: -18, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gAgriLst" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={CHART.lst} stopOpacity={0.2} />
                          <stop offset="100%" stopColor={CHART.lst} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gAgriNdvi" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={CHART.ndvi} stopOpacity={0.16} />
                          <stop offset="100%" stopColor={CHART.ndvi} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={CHART.grid} vertical={false} />
                      <XAxis dataKey="year" tick={{ fontSize: 9, fill: "#64748b" }} interval={5} tickLine={false} axisLine={{ stroke: "#e5e7eb" }} />
                      <YAxis yAxisId="lst" tick={{ fontSize: 9, fill: CHART.lst }} width={40} tickLine={false} axisLine={false} domain={["auto", "auto"]} tickFormatter={(v) => `${Math.round(v)}°`} />
                      <YAxis yAxisId="ndvi" orientation="right" domain={[0, 1]} width={26} tick={{ fontSize: 9, fill: CHART.ndvi }} tickLine={false} axisLine={false} />
                      <Tooltip contentStyle={{ background: "#f8faf9", border: "1px solid #e5e7eb", fontSize: 11, borderRadius: 6 }}
                        labelStyle={{ color: "#94a3b8" }} formatter={(v, n) => n === "LST °C" ? [`${v} °C`, n] : [v, n]} />
                      <Area yAxisId="lst" type="monotone" dataKey="lst" name="LST °C" stroke={CHART.lst} fill="url(#gAgriLst)" dot={false} strokeWidth={2} connectNulls isAnimationActive={false} activeDot={{ r: 3, strokeWidth: 0 }} />
                      <Area yAxisId="ndvi" type="monotone" dataKey="ndvi" name="NDVI" stroke={CHART.ndvi} fill="url(#gAgriNdvi)" dot={false} strokeWidth={2} connectNulls isAnimationActive={false} activeDot={{ r: 3, strokeWidth: 0 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                  <div className="flex gap-4 mt-1 text-[10px]"><span style={{ color: CHART.lst }}>— LST (°C)</span><span style={{ color: CHART.ndvi }}>— NDVI</span></div>
                </div>
              )}
            </div>
          </>
        ) : <div className="text-[12px] text-[#475569]">{loading ? "Calculando…" : "Sin datos."}</div>}
      </Sheet>
      <SheetToggles sheet={sheet} setSheet={setSheet} />
    </div>
  );
}
